import type {
  FeatureDef,
  FeatureOverrideState,
  ResolvedFeature,
  LaunchFlagView,
  FeatureOverrideOrg,
  StrandedOverride,
} from '@logspot-platform/platform-types';
import { api } from './api-client';

// The current org's effective flags — a plain `{ key: boolean }` map. Drives
// `useFeatures()` gating for any authed user (no super-admin required).
export type CurrentFeatures = Record<string, boolean>;

// Super-admin global-defaults view: the feature registry + whatever global
// overrides are set (keyed by feature key; absent key = inheriting code default).
export type AdminGlobalFeatures = {
  features: FeatureDef[];
  global: Record<string, FeatureOverrideState>;
};

// Super-admin per-org view: the registry plus the org's plan, the fully
// resolved effective values, and the raw org/global override maps that back
// the tri-state controls.
export type AdminOrgFeatures = {
  features: FeatureDef[];
  organization_id: string;
  plan: string;
  resolved: ResolvedFeature[];
  orgOverrides: Record<string, FeatureOverrideState>;
  globalOverrides: Record<string, FeatureOverrideState>;
};

type SuccessEnvelope<T> = { data: T };

export const FeaturesService = {
  // Current org's effective flags (any authed user).
  getFeatures: async (): Promise<CurrentFeatures> => {
    const result = await api.get<SuccessEnvelope<{ features: CurrentFeatures }>>(
      '/features',
    );
    return result.data.features;
  },

  // Super-admin: omit `organizationId` for the global-defaults view, pass one
  // for the per-org resolved view.
  getAdminFeatures: async (
    organizationId?: string,
  ): Promise<AdminGlobalFeatures | AdminOrgFeatures> => {
    const query = organizationId
      ? `?organization_id=${encodeURIComponent(organizationId)}`
      : '';
    const result = await api.get<
      SuccessEnvelope<AdminGlobalFeatures | AdminOrgFeatures>
    >(`/config/features${query}`);
    return result.data;
  },

  // Set or clear a single override. Omit `organizationId` (or pass '*') for the
  // global default; `value: null` clears the override = "Inherit".
  setFeatureOverride: async (input: {
    organizationId?: string;
    featureKey: string;
    value: boolean | null;
    reason?: string;
  }): Promise<void> => {
    const { organizationId, featureKey, value, reason } = input;
    await api.put('/config/features', {
      ...(organizationId ? { organization_id: organizationId } : {}),
      feature_key: featureKey,
      value,
      ...(reason ? { reason } : {}),
    });
  },

  // Launch-gated features and their global launch state (super-admin).
  getLaunchFlags: async (): Promise<LaunchFlagView[]> => {
    const result = await api.get<SuccessEnvelope<{ flags: LaunchFlagView[] }>>(
      '/config/launch-flags',
    );
    return result.data.flags;
  },

  // Open or close one feature's global launch gate.
  setLaunchFlag: async (input: {
    featureKey: string;
    launched: boolean;
    reason?: string;
  }): Promise<void> => {
    await api.put(
      `/config/launch-flags/${encodeURIComponent(input.featureKey)}`,
      {
        launched: input.launched,
        ...(input.reason ? { reason: input.reason } : {}),
      },
    );
  },

  // All per-org overrides grouped by feature key (the beta-override rosters).
  getAllFeatureOverrides: async (): Promise<
    Record<string, FeatureOverrideOrg[]>
  > => {
    const result = await api.get<
      SuccessEnvelope<{ overrides: Record<string, FeatureOverrideOrg[]> }>
    >('/config/feature-overrides');
    return result.data.overrides;
  },

  // Clear every per-org override on a feature (the post-GA sweep).
  clearFeatureOverrides: async (
    featureKey: string,
    reason?: string,
  ): Promise<number> => {
    const result = await api.post<SuccessEnvelope<{ cleared: number }>>(
      `/config/features/${encodeURIComponent(featureKey)}/overrides/clear`,
      reason ? { reason } : {},
    );
    return result.data.cleared;
  },

  // Override rows whose feature key is no longer in the registry.
  getStrandedOverrides: async (): Promise<StrandedOverride[]> => {
    const result = await api.get<
      SuccessEnvelope<{ overrides: StrandedOverride[] }>
    >('/config/stranded-overrides');
    return result.data.overrides;
  },

  // Delete all rows for a stranded key.
  clearStrandedOverride: async (featureKey: string): Promise<number> => {
    const result = await api.delete<SuccessEnvelope<{ cleared: number }>>(
      `/config/stranded-overrides/${encodeURIComponent(featureKey)}`,
    );
    return result.data.cleared;
  },
};
